// Company + project data that drives the homepage feed and case study pages.
// `id` values map to logo assets in ../assets and to the LogoMark component.
// Case study long-form content lives in ./caseStudyContent.js, keyed by
// `c{companyIndex}p{projectIndex}` (0-based array indices, NOT the `id` field).
//
// To show a real thumbnail on a project row (instead of the "Project image"
// placeholder), put the file in ./assets/projects/, import it up top, and
// add an `img` field to that project object, e.g.:
//
//   import ehsThriveImg from "../assets/projects/ehs-thrive.jpg";
//   ...
//   { title: "EHS Thrive", img: ehsThriveImg, ... }
//
// On hover, the image scales up and the title slides up over it — see
// .proj-img / .proj-img-caption in ../Portfolio2026.scss.
//
// `color` is any hex value — it's used as-is for the case-study hero
// background, and CSS derives lighter tints from it for the homepage
// image placeholder / metrics bar / mini-grid thumbnails (color-mix()
// in Portfolio2026.scss). One hex per company is all you need to set.
import ehsThriveThumbnail from "../assets/ehs/thrive_thumbnail.png";
import rasfThumbnail from "../assets/ehs/rasf/rasf_thumbnail.png";
import ccThumbnail from "../assets/cc/cc_thumbnail.png";
import ffAppThumbnail from "../assets/foodfight/ff_thumbnail.png";
import CosThumbnail from "../assets/cos/cos_thumbnail.png";
import GaugeThumbnail from "../assets/gauge/gauge_thumbnail.png";
import KitchrThumbnail from "../assets/kitchr/kitchr_thumbnail.png";

const companies = [
  {
    id: "c1",
    short: "U-M",
    name: "U-M EHS",
    meta: "2025 — present",
    color: "#FFCB05",
    projects: [
      {
        tag: "Intranet · 2025 · Professional",
        title: "EHS Thrive",
        desc: "Connected 104+ members across 10 expertise areas by replacing scattered platforms with a single source of truth.",
        role: "Lead Designer & Researcher & UX Developer",
        img: ehsThriveThumbnail,
        feat: false,
        hero: "Connecting 104 members to what matters most",
        heroSub:
          "EHS Thrive is a complimentary intranet site that replaced scattered platforms with one centralized digital experience.",
        rt: "8 min",
        metrics: [
          { n: "85%", l: "More site visits" },
          { n: "1.8×", l: "User interactions" },
          { n: "3.5×", l: "Session duration" },
        ],
      },
      {
        tag: "Informational Website · 2026 · Professional",
        title: "RASF 2026",
        img: rasfThumbnail,
        desc: "Designed the event hub for a $120K campus-wide safety fair, converting 76% of visitors into registrants.",
        role: "Lead UX Designer",
        feat: false,
        hero: "76% of visitors converted to registrants",
        heroSub:
          "An event information hub that made a $120K campus-wide safety fair clear and accessible for 4,000+ researchers.",
        rt: "6 min",
        metrics: [
          { n: "76%", l: "Visitor conversion" },
          { n: "93%", l: "Usefulness rating" },
          { n: "1/4", l: "Of outsourced cost" },
        ],
      },
      {
        tag: "Catalog · 2026 · Professional",
        title: "EHS Course Catalog",
        img: ccThumbnail,
        desc: "Centralized 40+ training courses for 5,000 researchers, reducing information-seeking time by 20-40 minutes.",
        role: "Lead UX Designer & Researcher",
        feat: false,
        hero: "Self-service training for 5,000 researchers",
        heroSub:
          "A centralized course catalog that cut 20-40 minutes of information-seeking per user.",
        rt: "7 min",
        metrics: [
          { n: "40+", l: "Courses catalogued" },
          { n: "23%", l: "Of support tickets addressed" },
          { n: "20-40", l: "Minutes saved per user" },
        ],
      },
      // {
      //   tag: "Interctive Chart · 2026 · Professional",
      //   title: "U-M Emergency Flipchart",
      //   desc: "Transformed physical emergency flip charts into a fully accessible digital experience across all devices.",
      //   role: "UX Designer & Accessibility Advocate",
      //   feat: false,
      //   hero: "Emergency safety, accessible to everyone",
      //   heroSub:
      //     "A digital flip chart that preserved familiarity while supporting screen readers and keyboard navigation.",
      //   rt: "5 min",
      //   metrics: [
      //     { n: "WCAG", l: "AA compliant" },
      //     { n: "All", l: "Devices supported" },
      //     { n: "100%", l: "Keyboard navigable" },
      //   ],
      // },
      // {
      //   tag: "Learning Experience · 2025",
      //   title: "DominKnow Learning Experience",
      //   desc: "Improved navigation, readability, and consistency across 12 e-learning courses for diverse university learners.",
      //   role: "UX & Learning Experience Designer",
      //   feat: false,
      //   hero: "A consistent learning experience across 12 courses",
      //   heroSub:
      //     "Redesigned e-learning layouts tested and validated with stakeholders across the university.",
      //   rt: "6 min",
      //   metrics: [
      //     { n: "12", l: "Courses redesigned" },
      //     { n: "A/B", l: "Tested & validated" },
      //     { n: "Scalable", l: "Framework built" },
      //   ],
      // },
    ],
  },
  {
    id: "c2",
    short: "FoodFight",
    name: "FoodFight Inc.",
    meta: "2023 — 2025",
    color: "#ea580c",
    projects: [
      {
        tag: "Social Gaming · 2024 · Professional",
        title: "FoodFight",
        img: ffAppThumbnail,
        desc: "Version 2.1 app overhaul with 10+ features shipped across B2C mobile app, consumer website, and B2B desktop platform.",
        role: "UX Developer & Researcher",
        feat: false,
        hero: "10+ features shipped in 11 months",
        heroSub:
          "Version 2.1 of FoodFight — unifying sports engagement and food ordering in a single, continuous user flow.",
        rt: "7 min",
        metrics: [
          { n: "10+", l: "Features shipped" },
          { n: "64%", l: "Faster user flows" },
          { n: "10K+", l: "Downloads" },
        ],
      },
      // {
      //   tag: "B2B · In progress",
      //   title: "FoodFight B2B Dashboard",
      //   desc: "B2B desktop web application for restaurant and business partners — case study coming soon.",
      //   role: "UX Developer",
      //   feat: false,
      //   hero: "Coming soon",
      //   heroSub: "This case study is currently being written.",
      //   rt: "— min",
      //   metrics: [],
      // },
    ],
  },
  // {
  //   id: "c3",
  //   short: "RB",
  //   name: "Retail & Business",
  //   meta: "2021 — present",
  //   color: "#15803d",
  //   projects: [
  //     {
  //       tag: "Product Design · 2024",
  //       title: "LuxCreo",
  //       desc: "Case study coming soon.",
  //       role: "—",
  //       feat: false,
  //       hero: "LuxCreo · 2024",
  //       heroSub: "Case study coming soon.",
  //       rt: "— min",
  //       metrics: [],
  //     },
  //     {
  //       tag: "Product Design · 2021",
  //       title: "KNBuilder",
  //       desc: "Case study coming soon.",
  //       role: "—",
  //       feat: false,
  //       hero: "KNBuilder · 2021",
  //       heroSub: "Case study coming soon.",
  //       rt: "— min",
  //       metrics: [],
  //     },
  //   ],
  // },
  {
    id: "c4",
    short: "Concept",
    name: "Concept Studies",
    meta: "Ongoing",
    color: "#0078FF",
    projects: [
      {
        tag: "FinTech · 16 weeks · Concept Study",
        title: "Cos Finance",
        img: CosThumbnail,
        desc: "End-to-end UX for a finance concept app for young professionals, with 38% faster task completion in usability testing.",
        role: "Lead Designer & UX Researcher",
        feat: false,
        hero: "38% faster task completion in usability testing",
        heroSub:
          "A finance-focused product concept grounded in real user input, designed to simplify everyday money management.",
        rt: "7 min",
        metrics: [
          { n: "38%", l: "Faster task completion" },
          { n: "80", l: "User insights" },
          { n: "5", l: "Design iterations" },
        ],
      },
      {
        tag: "B2B FinTech · 12 weeks · Concept Study",
        title: "Gauge",
        img: GaugeThumbnail,
        desc: "Research-driven SMB finance dashboard informed by interviews with 8 small business owners.",
        role: "Lead Designer & UX Researcher",
        feat: false,
        hero: "Simplifying finances for small and medium businesses",
        heroSub:
          "A research-driven conceptual dashboard designed around the real workflows and pain points of SMB owners.",
        rt: "6 min",
        metrics: [
          { n: "8", l: "Business interviews" },
          { n: "3", l: "Design iterations" },
          { n: "7", l: "Validated features" },
        ],
      },
      {
        tag: "Retail · 14 weeks · Concept Study",
        title: "Kitchr",
        img: KitchrThumbnail,
        desc: "Concept study exploring how local food communities can reduce waste, improve access to affordable meals, and foster social connection.",
        role: "Product Design & Research Lead",
        feat: false,
        hero: "Connecting food communities, reducing waste",
        heroSub:
          "A concept exploring how shared cooking and grocery coordination can make food more affordable and less wasteful.",
        rt: "5 min",
        metrics: [],
      },
    ],
  },
];

export default companies;
